import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles, RotateCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { IntegrationEmptyState } from "@/components/IntegrationEmptyState";

const STAGE_KEYS = ["stage1", "stage2", "stage3", "stage4"];
const STAGE_INTERVAL_MS = 1700;

/**
 * Autofill URL modal for brand or product. Phases:
 *   1. URL form
 *   2. Multi-stage progress (with cycling messages while we wait on the real API)
 *   3. Result → onSuccess(draft) and close
 *   4. Error: 412 firecrawl_not_configured / 502 llm_failed / 502 firecrawl_failed
 */
export function AutofillModal({
  open,
  onOpenChange,
  mode = "brand", // "brand" | "product"
  brandId, // required when mode === "product"
  brandName, // for the subtitle when mode === "product"
  onSuccess,
}) {
  const { t } = useTranslation(["autofill", "common", "errors"]);
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [errorKey, setErrorKey] = useState(null); // "firecrawl_not_configured" | "firecrawl_failed" | "llm_failed" | null
  const [errorText, setErrorText] = useState("");
  const stageTimer = useRef(null);

  // Reset when the modal opens/closes.
  useEffect(() => {
    if (!open) {
      stopStageCycling();
      setUrl("");
      setSubmitting(false);
      setStageIdx(0);
      setErrorKey(null);
      setErrorText("");
    }
  }, [open]);

  const startStageCycling = () => {
    stopStageCycling();
    setStageIdx(0);
    stageTimer.current = setInterval(() => {
      setStageIdx((i) => Math.min(i + 1, STAGE_KEYS.length - 1));
    }, STAGE_INTERVAL_MS);
  };

  const stopStageCycling = () => {
    if (stageTimer.current) {
      clearInterval(stageTimer.current);
      stageTimer.current = null;
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) return;
    setSubmitting(true);
    setErrorKey(null);
    setErrorText("");
    startStageCycling();

    try {
      const endpoint = mode === "product" ? "/autofill/product" : "/autofill/brand";
      const body =
        mode === "product"
          ? { url: url.trim(), brand_id: brandId }
          : { url: url.trim() };
      const { data } = await api.post(endpoint, body);
      stopStageCycling();
      onSuccess?.({ draft: data, source_url: url.trim() });
    } catch (err) {
      stopStageCycling();
      const detail = err?.response?.data?.detail || "";
      // Backend emits flat strings like "firecrawl_not_configured", "llm_failed", "firecrawl_failed".
      if (typeof detail === "string" && detail) {
        setErrorKey(detail);
      } else {
        setErrorKey("generic");
        setErrorText(formatApiError(err, t("errors:generic")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setErrorKey(null);
    setErrorText("");
    setStageIdx(0);
  };

  const titleNs = mode === "product" ? "autofill:modal.product" : "autofill:modal.brand";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`autofill-modal-${mode}`}
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-semibold tracking-tight">
            <Sparkles className="h-4 w-4 text-indigo-600" strokeWidth={2} />
            {t(`${titleNs}.title`)}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {mode === "product"
              ? t("autofill:modal.product.subtitle", { brand: brandName || "—" })
              : t("autofill:modal.brand.subtitle")}
          </DialogDescription>
        </DialogHeader>

        {/* ─── State: error ─── */}
        {errorKey === "firecrawl_not_configured" ? (
          <div className="space-y-4">
            <IntegrationEmptyState integration="firecrawl" variant="compact" />
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="autofill-modal-close"
              >
                {t("common:close")}
              </Button>
            </DialogFooter>
          </div>
        ) : errorKey === "llm_failed" ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
              <p className="font-display text-sm font-semibold text-rose-900">
                {t("errors:llm_failed.title")}
              </p>
              <p className="mt-1 text-xs text-rose-800/80">{t("errors:llm_failed.body")}</p>
            </div>
            <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="autofill-modal-close"
              >
                {t("common:close")}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  reset();
                  submit();
                }}
                data-testid="autofill-modal-retry"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                <RotateCw className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
                {t("errors:llm_failed.retry")}
              </Button>
            </DialogFooter>
          </div>
        ) : errorKey === "firecrawl_failed" ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
              <p className="font-display text-sm font-semibold text-rose-900">
                {t("errors:firecrawl_failed.title")}
              </p>
              <p className="mt-1 text-xs text-rose-800/80">{t("errors:firecrawl_failed.body")}</p>
            </div>
            <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="autofill-modal-close"
              >
                {t("common:close")}
              </Button>
              <Button
                type="button"
                onClick={reset}
                data-testid="autofill-modal-retry"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                <RotateCw className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
                {t("errors:firecrawl_failed.retry")}
              </Button>
            </DialogFooter>
          </div>
        ) : errorKey ? (
          // Generic fallback
          <div className="space-y-4">
            <div
              role="alert"
              data-testid="autofill-modal-error"
              className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900"
            >
              {errorText || t("errors:generic")}
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="autofill-modal-close"
              >
                {t("common:close")}
              </Button>
            </DialogFooter>
          </div>
        ) : submitting ? (
          // ─── State: progress ───
          <div className="space-y-4 py-4" data-testid="autofill-modal-progress">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-600" strokeWidth={2} />
              <span className="text-sm font-medium text-slate-800">
                {t(`autofill:progress.${STAGE_KEYS[stageIdx]}`)}
              </span>
            </div>
            <div className="space-y-2">
              {STAGE_KEYS.map((k, i) => (
                <div
                  key={k}
                  className={`flex items-center gap-2 text-xs ${
                    i < stageIdx
                      ? "text-emerald-700"
                      : i === stageIdx
                        ? "text-slate-700"
                        : "text-slate-400"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      i < stageIdx
                        ? "bg-emerald-500"
                        : i === stageIdx
                          ? "animate-pulse bg-indigo-500"
                          : "bg-slate-300"
                    }`}
                  />
                  {t(`autofill:progress.${k}`)}
                </div>
              ))}
            </div>
          </div>
        ) : (
          // ─── State: form ───
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label
                htmlFor="autofill-url"
                className="text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                {t(`${titleNs}.urlLabel`)}
              </Label>
              <Input
                id="autofill-url"
                type="url"
                inputMode="url"
                required
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t(`${titleNs}.urlPlaceholder`)}
                data-testid="autofill-modal-url-input"
                className="h-11"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="autofill-modal-cancel"
              >
                {t(`${titleNs}.cancel`)}
              </Button>
              <Button
                type="submit"
                data-testid="autofill-modal-submit"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                <Sparkles className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
                {t(`${titleNs}.submit`)}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
